import React from 'react';
import { useStore } from '../../store';
import { getSymbolContext } from '../../selectors/symbolContext';
import { ScoreBar } from '../common/panelPrimitives';

export function RegimePanel({ symbol }: { symbol: string }) {
  const { symbolData, settings, weights, setWeights } = useStore();
  const data = symbolData[symbol];
  if (!data?.candles?.length) return (
    <div className="card">
      <div className="sl">Regime Engine</div>
      <div className="empty"><div className="icon">🎯</div><h3>No Data</h3><p>Import OHLCV data to enable regime classification.</p></div>
    </div>
  );

  const ctx = getSymbolContext(symbol, data, settings, weights);
  const regime = ctx?.regime ?? null;
  const gamma  = ctx?.gamma  ?? null;
  if (!regime) return null;

  const domColors: Record<string, string> = { meanReversion: 'var(--green)', breakout: 'var(--blue)', trend: 'var(--purple)', noTrade: 'var(--amber)' };
  const bc = domColors[regime.dominant] || 'var(--blue)';

  return (
    <div className="card">
      <div className="sl">Regime Engine</div>
      <div className="card-inner mb12" style={{ borderLeft: `3px solid ${bc}` }}>
        <div className="fsb mb6">
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--t0)' }}>{regime.label}</div>
            <div style={{ fontSize: 13, color: bc, marginTop: 2, fontWeight: 500 }}>{regime.posture}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 10, color: 'var(--t3)', textTransform: 'uppercase', marginBottom: 2 }}>Confidence</div>
            <div className="mono" style={{ fontSize: 24, fontWeight: 700, color: bc }}>{regime.confidence}%</div>
          </div>
        </div>
        {!gamma && <div style={{ fontSize: 12, color: 'var(--amber)', marginTop: 4 }}>⚠ No options data — gamma component excluded, confidence reduced</div>}
      </div>

      <div className="sl">Score Breakdown</div>
      <div style={{ marginBottom: 12 }}>
        <ScoreBar label="Mean Reversion" value={regime.scores.meanReversion} color="var(--green)" />
        <ScoreBar label="Breakout" value={regime.scores.breakout} color="var(--blue)" />
        <ScoreBar label="Trend Continuation" value={regime.scores.trend} color="var(--purple)" />
        <ScoreBar label="No Trade" value={regime.scores.noTrade} color="var(--amber)" />
      </div>

      <div className="sl">Top Drivers</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 }}>
        {regime.topDrivers.map((d, i) => (
          <div key={i} className="card-inner" title={d.explanation}>
            <div className="fsb">
              <div>
                <span style={{ fontSize: 13, color: 'var(--t1)', textTransform: 'capitalize' }}>{d.key.replace(/([A-Z])/g, ' $1')}</span>
                <span style={{ fontSize: 12, color: 'var(--t2)', marginLeft: 8 }}>{d.label}</span>
              </div>
              <div className="flex gap6">
                <span style={{ fontSize: 11, color: 'var(--t3)' }}>w={d.weight}</span>
                <span className="mono" style={{ fontSize: 12, color: bc }}>{d.impact}</span>
              </div>
            </div>
            <div className="inferred">{d.explanation}</div>
          </div>
        ))}
      </div>

      <div className="sl">Scoring Weights (live-wired)</div>
      <div className="g2" style={{ gap: 6 }}>
        {Object.entries(weights).map(([k, v]) => (
          <div key={k} className="card-inner">
            <div className="fsb" style={{ marginBottom: 3 }}>
              <span style={{ fontSize: 11, color: 'var(--t2)', textTransform: 'capitalize' }}>{k.replace(/([A-Z])/g, ' $1')}</span>
              <span className="mono" style={{ fontSize: 11, color: 'var(--blue)' }}>{v}</span>
            </div>
            <input type="range" min="0" max="50" step="1" value={v} onChange={e => setWeights({ [k]: +e.target.value })} />
          </div>
        ))}
      </div>
    </div>
  );
}
